import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { scoreLabel } from "@/lib/constants";

export default function Admin() {
  const [tab, setTab] = useState("pending");
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});

  const load = useCallback(async () => {
    const [l, s, u, ld] = await Promise.all([
      api.get(`/admin/listings${tab === "all" ? "" : `?status=${tab}`}`),
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/admin/leads"),
    ]);
    setListings(l.data); setStats(s.data); setUsers(u.data); setLeads(ld.data);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const publish = async (id) => { await api.post(`/admin/listings/${id}/publish`); toast.success("Publiée"); load(); };
  const reject = async (id) => { await api.post(`/admin/listings/${id}/reject`); toast.success("Refusée"); load(); };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12">
      <div className="mb-8">
        <div className="label-mono mb-2 text-amber-500">§ back-office</div>
        <h1 className="font-display text-4xl md:text-5xl">Administration</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-zinc-800 border border-zinc-800 mb-12">
        {[
          ["Users", stats.total_users],
          ["Annonces", stats.total_listings],
          ["Non vérifiées", stats.unverified_listings],
          ["En validation", stats.pending_listings],
          ["Publiées", stats.published_listings],
          ["Leads", stats.total_leads],
        ].map(([k, v]) => (
          <div key={k} className="bg-zinc-950 p-5">
            <div className="label-mono mb-2">{k}</div>
            <div className="font-display text-3xl font-mono-tech">{v || 0}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-6 border-b border-zinc-800 mb-6 overflow-x-auto">
        {[["unverified", "Non vérifiées"], ["pending", "À valider"], ["published", "Publiées"], ["rejected", "Refusées"], ["all", "Toutes"], ["users", "Utilisateurs"], ["leads", "Leads"]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`pb-3 text-sm uppercase tracking-widest ${tab === t ? "border-b-2 border-amber-500 text-amber-500" : "text-zinc-500 hover:text-zinc-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "users" ? (
        <div className="border border-zinc-800 divide-y divide-zinc-800">
          {users.map((u) => (
            <div key={u.id} className="p-4 flex justify-between">
              <div>
                <div className="font-display text-lg">{u.name}</div>
                <div className="label-mono">{u.email}</div>
              </div>
              <div className="label-mono text-amber-500">{u.role}</div>
            </div>
          ))}
        </div>
      ) : tab === "leads" ? (
        <div className="border border-zinc-800 divide-y divide-zinc-800">
          {leads.map((ld) => (
            <div key={ld.id} className="p-4">
              <div className="font-display">{ld.name} → {ld.listing_title}</div>
              <div className="label-mono">{ld.email} · {new Date(ld.created_at).toLocaleDateString("fr-FR")}</div>
              <div className="text-sm text-zinc-400 mt-1">{ld.message}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-zinc-800 divide-y divide-zinc-800">
          {listings.length === 0 && <div className="p-10 label-mono text-center">Aucune annonce</div>}
          {listings.map((l) => {
            const s = scoreLabel(l.score || 0);
            return (
              <div key={l.id} className="p-4 flex items-center gap-4" data-testid={`admin-listing-${l.id}`}>
                <div className="flex-1">
                  <div className="font-display text-lg">{l.title}</div>
                  <div className="label-mono">{l.city} · {l.surface_exploitable || l.surface_total}m² · par {l.owner_name}</div>
                </div>
                <div className={`font-mono-tech text-sm ${s.color}`}>[ {l.score || 0} ]</div>
                {tab === "pending" && (
                  <>
                    <button onClick={() => publish(l.id)} data-testid={`publish-${l.id}`} className="bg-emerald-500 text-black px-4 py-2 text-xs uppercase tracking-widest hover:bg-emerald-400">Publier</button>
                    <button onClick={() => reject(l.id)} data-testid={`reject-${l.id}`} className="border border-red-500/50 text-red-400 px-4 py-2 text-xs uppercase tracking-widest hover:bg-red-500/10">Rejeter</button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
